import { formatEuro, UNIT_COUNT_BUCKETS } from '@brimo/shared';
import type { AllUnitsBudget, AllUnitsFilter, AllUnitsSort, UnitFilter } from '../types';

/**
 * „Alle Einheiten" (`/properties/units?…`): the filters live in the URL — amounts in whole euros (`priceMin`,
 * `priceMax`, `budget`), areas in m² (`areaMin`, `areaMax`), `q`, `propertyId`, `availability`, `contactId`, `sort`.
 * The page converts them to the cents/m² query of `GET /units`.
 */

export const ALL_UNITS_SORT_OPTIONS: { value: AllUnitsSort; label: string }[] = [
  { value: 'property', label: 'Objekt' },
  { value: 'number', label: 'Einheit' },
  { value: 'price', label: 'Kaufpreis' },
  { value: 'area', label: 'Wohnfläche' },
  { value: 'rent', label: 'Soll-Miete' },
];

const SORTS = new Set<string>(ALL_UNITS_SORT_OPTIONS.map((option) => option.value));
const BUCKETS = new Set<string>(UNIT_COUNT_BUCKETS);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A non-negative number from the URL („250000", „54,5"); undefined when empty or unreadable. */
export function readNumber(value: string | null): number | undefined {
  if (value === null || value.trim() === '') return undefined;
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

function euros(value: string | null): number | undefined {
  const parsed = readNumber(value);
  return parsed === undefined ? undefined : Math.round(parsed * 100);
}

/** The query of `GET /units` from the page's URL parameters. */
export function filterFromParams(params: URLSearchParams): AllUnitsFilter {
  const availability = params.get('availability') ?? '';
  const sort = params.get('sort') ?? '';
  const propertyId = params.get('propertyId') ?? '';
  const contactId = params.get('contactId') ?? '';
  const q = (params.get('q') ?? '').trim();
  return {
    q: q === '' ? undefined : q,
    propertyId: UUID.test(propertyId) ? propertyId : undefined,
    availability: BUCKETS.has(availability) ? (availability as UnitFilter) : 'all',
    priceMinCents: euros(params.get('priceMin')),
    priceMaxCents: euros(params.get('priceMax')),
    areaMinSqm: readNumber(params.get('areaMin')),
    areaMaxSqm: readNumber(params.get('areaMax')),
    budgetCents: euros(params.get('budget')),
    contactId: UUID.test(contactId) ? contactId : undefined,
    sort: SORTS.has(sort) ? (sort as AllUnitsSort) : 'property',
  };
}

/** Number of active filters for „Filter zurücksetzen" (sort and availability tabs do not count). */
export function activeFilterCount(filter: AllUnitsFilter): number {
  return [
    filter.q,
    filter.propertyId,
    filter.priceMinCents,
    filter.priceMaxCents,
    filter.areaMinSqm,
    filter.areaMaxSqm,
    filter.budgetCents,
    filter.contactId,
  ].filter((value) => value !== undefined).length;
}

/** The chip of the selected contact: „Budget 285.000 € · Selbstauskunft V3" or „Kein Budget hinterlegt". */
export function budgetChipLabel(budget: AllUnitsBudget | null): string {
  return budget === null
    ? 'Kein Budget hinterlegt'
    : `Budget ${formatEuro(budget.budgetCents)} · ${budget.source}`;
}

/** The calculator of a unit, with the selected contact when there is one. */
export function calculatorHref(propertyId: string, unitId: string, contactId: string | undefined): string {
  const search = new URLSearchParams({ unitId });
  if (contactId !== undefined) search.set('contactId', contactId);
  return `/properties/${propertyId}/calculator?${search.toString()}`;
}
